import { pathLength, pipeLatLngs } from '../lib/model';

export const HA_DN = [20, 25, 32, 40, 50, 63];
export const HA_MAT = ['PE-HD', 'PE-Xa', 'Kupfer', 'Stahl', 'PVC'];
import { isRot } from '../lib/fitSymbols';

export { isRot };
const angDiff = (a, b) => Math.abs((((a - b) % 360) + 540) % 360 - 180);
// cross/tee: turn the arms onto the pipes that are connected to the fitting (unless rotated by hand)
export function autoAngle(f, pipes = [], fittings = []) {
  const inline = ['valve', 'prv', 'pump'].includes(f.type);
  if ((!isRot(f) && !inline) || f.angleManual) return null;
  const k = Math.cos((f.lat * Math.PI) / 180), dirs = [];
  pipes.forEach((pp) => {
    const pts = pp.points || [];
    pts.forEach((pt, i) => {
      if (pt?.fid !== f.id) return;
      [pts[i - 1], pts[i + 1]].forEach((nb) => {
        const q = nb && pipeLatLngs({ points: [nb] }, fittings)[0];
        if (q && (q[0] !== f.lat || q[1] !== f.lon)) dirs.push((Math.atan2((q[1] - f.lon) * k, q[0] - f.lat) * 180) / Math.PI);
      });
    });
  });
  if (!dirs.length) return null;
  const arms = f.type === 'cross4' ? [0, 90, 180, 270] : inline ? [90, 270] : [0, 90, 180];
  let best = null;
  dirs.forEach((d) => arms.forEach((a) => {
    const c = (((d - a) % 360) + 360) % 360, cost = dirs.reduce((s, x) => s + Math.min(...arms.map((r) => angDiff(x, c + r))), 0);
    if (!best || cost < best.cost - 1e-6) best = { c, cost };
  }));
  return Math.round(best.c * 10) / 10;
}
export const needsRed = (f) => f.hostPipe && (['tee3', 'cross4', 'house'].includes(f.type) || ['AB', 'AS', 'T'].includes(f.sym));
export const redText = (f, t) => (f.reduction ? `${t.redLabel} DN ${f.hostDn || '?'} ➔ HA DN ${f.reduction.dn} ${f.reduction.mat}` : '');

// rotation, reduction (main ➔ house connection) and private-route flags
export function FittingExtras({ t, obj, patch, project }) {
  const a = obj.angle || 0;
  const rot = (d) => patch({ angle: (((a + d) % 360) + 360) % 360, angleManual: true });
  // turn the symbol into the run of the nearest pipe
  const align = () => {
    const auto = autoAngle({ ...obj, angleManual: false }, project?.pipes, project?.fittings);
    if (auto != null) return patch({ angle: Math.round(auto), angleManual: false });
    const k = Math.cos((obj.lat * Math.PI) / 180), xy = (p) => [(p[1] - obj.lon) * 111320 * k, (p[0] - obj.lat) * 111320];
    let best = null;
    (project?.pipes || []).forEach((pp) => { const pts = pipeLatLngs(pp, project.fittings || []).map(xy); for (let i = 1; i < pts.length; i++) {
      const [ax, ay] = pts[i - 1], [bx, by] = pts[i], dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy || 1, u = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L2)), d = Math.hypot(ax + u * dx, ay + u * dy);
      if (!best || d < best.d) best = { d, brg: ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360 };
    } });
    if (!best) return;
    const b = Math.round(best.brg), tx = ((((b - 90) % 180) + 180) % 180) > 90 ? ((((b - 90) % 180) + 180) % 180) + 180 : (((b - 90) % 180) + 180) % 180;
    patch({ angle: obj.type === 'cross4' ? b % 90 : isRot(obj) ? b : tx % 360 });
  };
  const red = obj.reduction || {};
  return (
    <>
      {(
        <div className="fx-box" data-testid="rotate-box">
          <b>🔄 {t.rotTitle}: <span data-testid="rotate-val">{a}°</span></b>
          <div className="row">
            <button className="mini" data-testid="rotate-minus15" onClick={() => rot(-15)}>-15°</button>
            <input type="range" min="0" max="359" value={a} data-testid="rotate-slider" onChange={(e) => patch({ angle: +e.target.value, angleManual: true })} />
            <button className="mini" data-testid="rotate-plus15" onClick={() => rot(15)}>+15°</button>
            <button className="mini" data-testid="rotate-plus90" onClick={() => rot(90)}>+90°</button>
            <button className="mini" data-testid="rotate-align-btn" onClick={align}>📐 {t.rotAlign}</button>
          </div>
        </div>
      )}
      {(needsRed(obj) || obj.reduction) && (
        <div className="fx-box fx-red" data-testid="reduction-box">
          <b>🔄 {t.redTitle}</b>
          <div className="row">
            <span>{t.redFrom} DN {obj.hostDn || '?'} ➔</span>
            <select data-testid="reduction-dn-select" value={red.dn || ''} onChange={(e) => patch({ reduction: { ...red, dn: +e.target.value, mat: red.mat || 'PE-HD' } })}>
              <option value="">HA DN…</option>{HA_DN.map((d) => <option key={d} value={d}>DN {d}</option>)}
            </select>
            <select data-testid="reduction-mat-select" value={red.mat || ''} onChange={(e) => patch({ reduction: { ...red, mat: e.target.value, dn: red.dn || 32 } })}>
              <option value="">{t.redMat}…</option>{HA_MAT.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {obj.reduction && <small data-testid="reduction-text">{redText(obj, t)}</small>}
        </div>
      )}
      <label className="fx-check"><input type="checkbox" data-testid="priv-unknown-check" checked={!!obj.privUnknown} onChange={(e) => patch({ privUnknown: e.target.checked })} /> ⚠️ {t.privUnknown}</label>
    </>
  );
}

export function PipeExtras({ t, obj, patch, fittings }) {
  const len = pathLength(pipeLatLngs(obj, fittings));
  return (
    <div className="fx-box" data-testid="pipe-private-box">
      <label className="fx-check"><input type="checkbox" data-testid="pipe-private-check" checked={!!obj.private} onChange={(e) => patch({ private: e.target.checked })} /> <i className="dash-sample" aria-hidden="true" /> {t.privPipe}</label>
      {obj.private && <label>📏 {t.privLen}<input inputMode="decimal" data-testid="pipe-privlen-input" value={obj.privLen ?? ''} placeholder={len.toLocaleString(t.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} onChange={(e) => patch({ privLen: e.target.value })} /></label>}
      <small data-testid="pipe-length-split">{obj.private ? `${t.lenPrivate}: ${obj.privLen || len.toLocaleString(t.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m` : `${t.lenPublic}: ${len.toLocaleString(t.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`}</small>
    </div>
  );
}
